import { apiClient } from '@/lib/api/client'
import { config } from '@/lib/config'
import { PLATFORM_LICENSE } from '@/lib/constants'
import { create } from 'zustand'

interface RuntimeConfig {
  mlops_enabled: boolean
  site_notice_enabled: boolean
  site_notice_text: string
  site_name: string
  site_operator_label: string
  /** 큐레이션 ZIP 을 무디스크 스트리밍으로 받을지. 꺼져 있으면 기존 Celery export. */
  curation_download_streaming?: boolean
  /** 데이터셋 업로드 파일 개수 상한. 선택 직후 사전 검증에 쓴다. */
  dataset_upload_max_files?: number
  /** 플랫폼 라이선스 (system_settings). */
  platform_license?: string
  /** 플랫폼이 내려주는 CLI wheel 주소. */
  cli_wheel_url?: string
  /** 플랫폼 삭제 때 HF 저장소를 7일 뒤(true) · 바로(false) 지운다 (2026-10-24 이슈 #15 · #16). */
  hf_delete_grace_enabled?: boolean
  hf_delete_grace_days?: number
}

interface FeatureFlagsState {
  isLoading: boolean
  loaded: boolean
  mlopsEnabled: boolean
  siteNoticeEnabled: boolean
  siteNoticeText: string
  siteName: string
  siteOperatorLabel: string
  curationDownloadStreaming: boolean
  /** 업로드 파일 개수 상한. 서버 응답 전에는 보수적인 기본값을 쓴다. */
  datasetUploadMaxFiles: number
  /** 플랫폼 라이선스. 서버 응답 전에는 constants 의 기본값. */
  platformLicense: string
  /** CLI 설치 파일 주소. 서버 응답 전에는 빈 값 — 화면은 파일 이름 안내로 대신한다. */
  cliWheelUrl: string
  /** 삭제 확인 창 안내용. 서버 응답 전에는 기본값(바로 삭제). */
  hfDeleteGraceEnabled: boolean
  hfDeleteGraceDays: number
  setMlopsEnabled: (enabled: boolean) => void
  setPublicSettings: (
    settings: Pick<
      RuntimeConfig,
      'site_notice_enabled' | 'site_notice_text' | 'site_name' | 'site_operator_label'
    >,
  ) => void
  fetchRuntimeConfig: () => Promise<void>
}

/** 브라우저 탭 제목도 관리자 설정의 사이트 이름을 따른다 (2026-10-20 이슈 #8). */
function setDocumentTitle(name: string | undefined): void {
  if (name && typeof document !== 'undefined') document.title = name
}

export const useFeatureFlagsStore = create<FeatureFlagsState>((set) => ({
  isLoading: false,
  loaded: false,
  mlopsEnabled: config.mlopsEnabled,
  siteNoticeEnabled: true,
  siteNoticeText: '개발 시험 중이며, 공식 공개는 2026년 12월 예정입니다.',
  siteName: 'K-Humanoid Hub',
  siteOperatorLabel: 'ETRI-KIMM',
  curationDownloadStreaming: false,
  // 서버가 알려주기 전 기본값. backend app/config.py 기본값과 맞춘다.
  datasetUploadMaxFiles: 5000,
  platformLicense: PLATFORM_LICENSE,
  cliWheelUrl: '',
  hfDeleteGraceEnabled: false,
  hfDeleteGraceDays: 7,
  setMlopsEnabled: (enabled) => set({ mlopsEnabled: enabled, loaded: true }),
  setPublicSettings: (settings) => {
    setDocumentTitle(settings.site_name)
    set({
      siteNoticeEnabled: settings.site_notice_enabled,
      siteNoticeText: settings.site_notice_text,
      siteName: settings.site_name,
      siteOperatorLabel: settings.site_operator_label,
    })
  },
  fetchRuntimeConfig: async () => {
    set({ isLoading: true })
    try {
      const data = await apiClient.get<RuntimeConfig>('/config')
      setDocumentTitle(data.site_name)
      set({
        loaded: true,
        isLoading: false,
        mlopsEnabled: data.mlops_enabled,
        siteNoticeEnabled: data.site_notice_enabled,
        siteNoticeText: data.site_notice_text,
        siteName: data.site_name,
        siteOperatorLabel: data.site_operator_label,
        hfDeleteGraceEnabled: data.hf_delete_grace_enabled ?? false,
        hfDeleteGraceDays: data.hf_delete_grace_days ?? 7,
        curationDownloadStreaming: Boolean(data.curation_download_streaming),
        // 구버전 서버는 이 필드를 안 준다 — 그 경우 기본값을 유지한다.
        ...(typeof data.dataset_upload_max_files === 'number' && data.dataset_upload_max_files > 0
          ? { datasetUploadMaxFiles: data.dataset_upload_max_files }
          : {}),
        ...(data.platform_license ? { platformLicense: data.platform_license } : {}),
        ...(data.cli_wheel_url ? { cliWheelUrl: data.cli_wheel_url } : {}),
      })
    } catch {
      set({ loaded: true, isLoading: false })
    }
  },
}))

/** 플랫폼 라이선스 — 화면에 상수로 박지 않고 서버 설정을 따른다. */
export function usePlatformLicense(): string {
  return useFeatureFlagsStore((s) => s.platformLicense)
}

/** CLI 설치 파일 주소 — 플랫폼이 직접 내려준다 (2026-10-13). 모르면 빈 문자열. */
export function useCliWheelUrl(): string {
  return useFeatureFlagsStore((s) => s.cliWheelUrl)
}
